import { ReactNode, useEffect, useState } from 'react';

import { BottomNav, FitToViewport, Navigation } from '@quizzach/components';
import { BottomNavMobile } from '@quizzach/layouts';
import { useWindowListener } from '@quizzach/contexts';
import { IQuestion } from '@quizzach/interfaces';
import { BackgroundContainer, Colors } from '@quizzach/styles';
import {
	FramedCover,
	NavPlayProgress,
	PremadeQuizPlayBackground,
	ProgressBar,
	QuestionNumber,
	QuizContainer,
	QuizNavigator,
	QuizSlidesContainer,
} from '@quizzach/play-styles';

// Fallback slide position while the real position in the question
// database is still loading.
export const PREVIEW_QUESTION_NUMBER = 17;
export const PREVIEW_TOTAL_QUESTIONS = 20;

interface IAdminPreviewStageProps {
	question: IQuestion;
	dataId: string;
	onNext: VoidFunction;
	onPrevious: VoidFunction;
	/** Position of the previewed question in the full database. */
	position?: number;
	total?: number;
	children: ReactNode;
}

// Replicates the redesigned play chrome (framed cover backdrop, top play
// bar with progress, mobile navigator pill) around a single admin slide.
export const AdminPreviewStage = ({
	question,
	dataId,
	onNext,
	onPrevious,
	position = PREVIEW_QUESTION_NUMBER,
	total = PREVIEW_TOTAL_QUESTIONS,
	children,
}: IAdminPreviewStageProps) => {
	const { touchDevice } = useWindowListener();
	const [coverLoaded, setCoverLoaded] = useState(false);

	useEffect(() => {
		if (touchDevice) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
			const target = event.target as HTMLElement | null;
			if (
				target &&
				(target.isContentEditable ||
					['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
			) {
				return;
			}
			// mounted-but-hidden modals keep empty portal containers in #modal,
			// so only a container WITH content means a dialog is actually open
			const modalRoot = document.getElementById('modal');
			if (
				modalRoot &&
				Array.from(modalRoot.children).some(
					(child) => child.childElementCount > 0
				)
			) {
				return;
			}
			if (event.key === 'ArrowRight') onNext();
			else onPrevious();
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [touchDevice, onNext, onPrevious]);

	const progressPercentage = total > 0 ? (position / total) * 100 : 0;

	// One indicator, two homes: inside QuizNavigator on mobile, inside the
	// top play bar (NavPlayProgress) on desktop — CSS picks which one shows.
	const navigatorIndicator = (
		<div className='quiz-navigator-indicator'>
			<div className='quiz-navigator-numbers'>
				<QuestionNumber>{position}</QuestionNumber>
				<QuestionNumber $small>/ {total}</QuestionNumber>
			</div>
		</div>
	);

	return (
		<BackgroundContainer
			color={Colors.quizBackground}
			style={{ paddingBottom: 0 }}
		>
			<PremadeQuizPlayBackground>
				<div className='quiz-play-background-content'>
					<FramedCover aria-hidden='true'>
						<img
							src='/quiz-play-backgrounds/default_play_cover.webp'
							alt=''
							className={coverLoaded ? 'loaded' : undefined}
							onLoad={() => setCoverLoaded(true)}
						/>
					</FramedCover>
					<Navigation
						hideSecondaryItems
						bareTopNav
						topNavCenter={
							<NavPlayProgress>
								<ProgressBar $progress={progressPercentage} />
								{navigatorIndicator}
							</NavPlayProgress>
						}
					/>

					<QuizContainer className='flex-column noselect'>
						<QuizNavigator>
							{navigatorIndicator}
							<ProgressBar $progress={progressPercentage} />
						</QuizNavigator>
						{touchDevice && (
							<QuizSlidesContainer data-id={`${dataId}-mobile`}>
								{/* Mobile scrolls naturally; the fit-to-viewport scaling
								    is a desktop/presentation concern. */}
								<div>{children}</div>
								<BottomNavMobile
									question={question}
									handleNext={onNext}
									handlePrevious={onPrevious}
									isPremadeOrEstimation={true}
									type='plain'
								/>
							</QuizSlidesContainer>
						)}
						{!touchDevice && (
							<QuizSlidesContainer data-id={`${dataId}-desktop`}>
								<FitToViewport>{children}</FitToViewport>
								<BottomNav
									question={question}
									handleNext={onNext}
									handlePrevious={onPrevious}
									isPremadeOrEstimation={true}
									type='plain'
								/>
							</QuizSlidesContainer>
						)}
					</QuizContainer>
				</div>
			</PremadeQuizPlayBackground>
		</BackgroundContainer>
	);
};
